import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import {
  Button,
  Container,
  Form,
  OverlayTrigger,
  Popover
} from 'react-angle-dashboard-components'
import { useHistory } from 'react-router-dom'
import { Paths } from '../../../routes/paths'
import { Table } from 'react-bootstrap'
import { quoteService } from '../../../services'
import filterIcon from '../../Quotes/filterIcon.svg'

import '../../Quotes/Quotes.css'
import { IGroupDTO, IGroupListQueryParams } from '../../../services/group/dto'
import { IAgenciesDTO, IBrokersDTO, IOfficesDTO } from '../../../services/quote/dto'
import { groupService } from '../../../services'

const tableTypeEnums = {
  groupAndBroker: 'group_and_broker',
  group: 'group'
}

const filterEnums = {
  agencyName: 'Agency Name',
  agencyOffice: 'Agency Office',
  brokerName: 'Broker Name'
}

export const Groups = () => {
  const history = useHistory()

  const groupEnums = {
    companyName: 'COMPANY NAME',
    agencyName: filterEnums.agencyName.toUpperCase(),
    agencyOffice: filterEnums.agencyOffice.toUpperCase(),
    brokerName: filterEnums.brokerName.toUpperCase(),
  }

  const [agencyFilter, setAgencyFilter] = useState<number | undefined>(undefined)
  const [brokerFilter, setBrokerFilter] = useState<number | undefined>(undefined)
  const [officeFilter, setOfficeFilter] = useState<number | undefined>(undefined)

  const [showFilterFormPopover, setShowFilterFormPopover] = useState(false)
  const [dataLoaded, setDataLoaded] = useState(false)
  const [tableType, setTableType] = useState(tableTypeEnums.group)

  const [companyNameFilter, setCompanyNameFilter] = useState('')
  const [filteredGroups, setFilteredGroups] = useState<IGroupDTO[]>([])

  const { data: groups } = useQuery(
    [
      'get-groups',
      agencyFilter,
      brokerFilter,
      officeFilter
    ],
    () => {
      const params: IGroupListQueryParams = {
        agencyId: agencyFilter,
        brokerId: brokerFilter,
        officeId: officeFilter
      }
      return groupService.getGroups(params)
    }
  )

  const { data: agencies } = useQuery<IAgenciesDTO[]>(
    ['agency-choices'],
    quoteService.getAgencies,
    { enabled: tableType === tableTypeEnums.groupAndBroker }
  )

  const { data: brokers } = useQuery<IBrokersDTO[]>(
    ['broker-choices'],
    quoteService.getBrokers,
    { enabled: tableType === tableTypeEnums.groupAndBroker }
  )

  const { data: offices } = useQuery<IOfficesDTO[]>(
    ['office-choices'],
    quoteService.getOffices,
    { enabled: tableType === tableTypeEnums.groupAndBroker }
  )

  useEffect(() => {
    if (groups) {

      if (!dataLoaded) {
        setDataLoaded(true)
      }
      
      if (groups.table_type !== tableType) {
        setTableType(groups.table_type)
      }

      setFilteredGroups(groups.data)
    }
  }, [groups, dataLoaded, tableType])

  useEffect(() => {
    if (agencies && agencies.length === 1) {
      setAgencyFilter(agencies[0].entryId)
    }
  }, [agencies])

  useEffect(() => {
    if (brokers && brokers.length === 1) {
      setBrokerFilter(brokers[0].entryId)
    }
  }, [brokers])

  useEffect(() => {
    if (offices && offices.length === 1) {
      setOfficeFilter(offices[0].entryId)
    }
  }, [offices])

  useEffect(() => {
    if (groups?.data) {
      setFilteredGroups(groups.data.filter((group: IGroupDTO) => 
        group.group_name.toLowerCase().includes(companyNameFilter)))
    }
  }, [companyNameFilter, groups])

  const handleRowClick = (groupId: string) => {
    history.push(`${Paths.groups}/${groupId}/summary`)
  }

  const onFilterBtnClick = () => {
    setShowFilterFormPopover(!showFilterFormPopover)
  }

  const onFilterCloseBtnClick = () => {
    setShowFilterFormPopover(!showFilterFormPopover)
  }

  const onFilterResetBtnClick = () => {
    setAgencyFilter(undefined)
    setBrokerFilter(undefined)
    setOfficeFilter(undefined)
  }

  const popoverFilterForm = (
    <Popover
      id="popover-basic"
      className="border shadow p-3 filter-popover-width"
      style={{ minWidth: 560 }}
    >
      <div className="container-flex">
        <div className="row no-gutters mx-2">
          <div className="col-12 col-sm-6">
            { agencies?.length !== 0 && (<Form.Group>
              <Form.Label>{filterEnums.agencyName}</Form.Label>
              <Form.Control
                as="select"
                onChange={(e) => setAgencyFilter(parseInt(e.target.value))}
                placeholder=""
                className="bg-white"
                value={agencyFilter}
              >
                {[{ entryId: undefined, name: '' }, ...(agencies || [])].map((agency) => (
                    <option value={agency.entryId} key={`agency-key-${agency.entryId}`}>
                      {agency.name}
                    </option>
                  ))}
              </Form.Control>
            </Form.Group>)}
            { brokers?.length !== 0 && (<Form.Group>
              <Form.Label>{filterEnums.brokerName}</Form.Label>
              <Form.Control
                as="select"
                onChange={(e) => setBrokerFilter(parseInt(e.target.value))}
                placeholder=""
                className="bg-white"
                value={brokerFilter}
              >
                {[{ entryId: undefined, fullName: '' }, ...(brokers || [])].map((broker) => (
                    <option value={broker.entryId} key={`broker-key-${broker.entryId}`}>
                      {broker.fullName}
                    </option>
                  ))}
              </Form.Control>
            </Form.Group>)}
          </div>
          <div className="col-12 col-sm-6">
            <div className="mx-2">
              { offices?.length !== 0 && (<Form.Group>
                <Form.Label>{filterEnums.agencyOffice}</Form.Label>
                <Form.Control
                  as="select"
                  onChange={(e) => setOfficeFilter(parseInt(e.target.value))}
                  placeholder=""
                  className="bg-white"
                  value={officeFilter}
                >
                  {[{ entryId: undefined, name: '' }, ...(offices || [])].map((office) => (
                      <option value={office.entryId} key={`office-key-${office.entryId}`}>
                        {office.name}
                      </option>
                    ))}
                </Form.Control>
            </Form.Group>)}
            </div>
          </div>
        </div>
        <div className="row no-gutters d-flex flex-row justify-content-end mx-2">
          <Button
            variant="outline-primary"
            onClick={onFilterResetBtnClick}
            className="mr-2"
          >
            Reset
          </Button>
          <Button type="submit" onClick={onFilterCloseBtnClick}>
            Close
          </Button>
        </div>
      </div>
    </Popover>
  )

  return (
    <>
      <div
        className="container-fluid px-md-10 px-3 mb-4"
        style={{ paddingTop: 50, paddingBottom: 50 }}
      >
        <div className="row no-gutters">
          <h1>Book of Business</h1>
        </div>
        <div className="row no-gutters">
          <p className="fw-semibold fs-6 font-color-gray">
            Search for your Company
          </p>
        </div>

        <div className="row no-gutters">
          <div className="col-sm-4 col-6">
            <Form.Group>
              <Form.Control
                placeholder="Search by company name"
                onChange={(e) => setCompanyNameFilter(e.target.value)}
                style={{
                  border: '0.0625rem solid #E7EAF3',
                  height: '48px',
                  backgroundColor: '#fff'
                }}
              />
            </Form.Group>
          </div>

          {tableType === tableTypeEnums.groupAndBroker && (<OverlayTrigger
            placement="bottom"
            delay={{ show: 250, hide: 400 }}
            overlay={popoverFilterForm}
            trigger="click"
            show={showFilterFormPopover}
          >
            <Button
              className="ml-2 bg-white text-secondary d-flex flex-row align-items-center"
              onClick={onFilterBtnClick}
              style={{
                border: '0.0625rem solid #E7EAF3',
                height: '48px'
              }}
            >
              <img src={filterIcon} className="h-24 mr-2" alt="filter" />
              <span className="d-none d-sm-block">Filters</span>
              <i
                className="icon fs-10 icon-dropdown ml-4"
                style={{
                  height: '20px',
                  width: '20px',
                  paddingLeft: '5px'
                }}
              />
            </Button>
          </OverlayTrigger>)}
        </div>
      </div>
      <Container
        fluid
        style={{ paddingTop: 50, paddingBottom: 100, marginTop: -90 }}
      >
        <Table bordered hover>
          <thead style={{ backgroundColor: '#F8F9FA' }}>
            <tr>
              <th
                style={{ border: 'none', paddingLeft: '60px' }}
              >
                {groupEnums.companyName}
              </th>
              { tableType === tableTypeEnums.groupAndBroker && (<th
                style={{ border: 'none' }}
              >
                {groupEnums.brokerName}
              </th>)}
              { tableType === tableTypeEnums.groupAndBroker && (<th
                style={{ border: 'none' }}
              >
                {groupEnums.agencyName}
              </th>)}
              { tableType === tableTypeEnums.groupAndBroker && (<th
                style={{ border: 'none' }}
              >
                {groupEnums.agencyOffice}
              </th>)}
            </tr>
          </thead>
          <tbody style={{ color: '#6C757D' }}>
            {filteredGroups.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center p-8">
                  <h4 className="text-muted">
                    <i>No companies found</i>
                  </h4>
                </td>
              </tr>
            )}
            {filteredGroups.map(
              (row: IGroupDTO, index: any) =>
                <React.Fragment key={`company-${row.group_id}-${index}`}>
                  <tr
                    onClick={() => handleRowClick(row.group_id)}
                  >
                    <td
                      style={{
                        border: 'none',
                        borderBottom:
                          '0.0625rem solid rgba(231, 234, 243, 0.7)',
                        color: '#212529',
                        fontWeight: '500',
                        display: 'flex'
                      }}
                    >
                      <i className={'icon icon-right-arrow text-primary'}/>
                      <div
                        style={{ display: 'flex', flexDirection: 'column' }}
                      >
                        {row?.group_name}
                      </div>
                    </td>
                    { tableType === tableTypeEnums.groupAndBroker && (<td
                      style={{
                        border: 'none',
                        borderBottom:
                          '0.0625rem solid rgba(231, 234, 243, 0.7)',
                        verticalAlign: 'middle'
                      }}
                    >
                      {row?.broker_name}
                    </td>)}
                    { tableType === tableTypeEnums.groupAndBroker && (<td
                      style={{
                        border: 'none',
                        borderBottom:
                          '0.0625rem solid rgba(231, 234, 243, 0.7)',
                        verticalAlign: 'middle'
                      }}
                    >
                      {row?.agency_name}
                    </td>)}
                    { tableType === tableTypeEnums.groupAndBroker && (<td
                      style={{
                        border: 'none',
                        borderBottom:
                          '0.0625rem solid rgba(231, 234, 243, 0.7)',
                        verticalAlign: 'middle'
                      }}
                    >
                      {row?.office_location}
                    </td>)}
                  </tr>
                </React.Fragment>
            )}
          </tbody>
        </Table>
      </Container>
    </>
  )
}
